import type { LatLngTuple } from "leaflet";
import type { Club } from "../types/club";

const geocodeCache = new Map<string, Promise<LatLngTuple | null>>();

function normalizeText(value: unknown) {
  return String(value || "").trim();
}

function isUsableCoordinatePair(latitude: number | null, longitude: number | null) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  return !(
    Math.abs(Number(latitude)) < 0.000001 &&
    Math.abs(Number(longitude)) < 0.000001
  );
}

export function buildClubAddressQuery(club: Club) {
  return [
    club.address,
    club.number,
    club.neighborhood,
    club.zipCode,
    club.city,
    club.state,
    club.country || "Brasil",
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(", ");
}

export function hasClubCoordinates(club: Club) {
  return isUsableCoordinatePair(club.latitude, club.longitude);
}

export function canResolveClubMapPosition(club: Club) {
  return Boolean(buildClubAddressQuery(club)) || hasClubCoordinates(club);
}

async function geocodeAddress(address: string) {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(
      address,
    )}`,
  );
  if (!response.ok) return null;
  const rows = await response.json();
  const first = Array.isArray(rows) ? rows[0] : null;
  const latitude = Number(first?.lat);
  const longitude = Number(first?.lon);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return [latitude, longitude] as LatLngTuple;
}

export async function resolveClubMapPosition(club: Club) {
  if (hasClubCoordinates(club)) {
    return [Number(club.latitude), Number(club.longitude)] as LatLngTuple;
  }

  const address = buildClubAddressQuery(club);
  if (address) {
    const cacheKey = `${club.id}:${address}`;
    if (!geocodeCache.has(cacheKey)) {
      geocodeCache.set(
        cacheKey,
        geocodeAddress(address).catch(() => null),
      );
    }
    const resolved = await geocodeCache.get(cacheKey);
    if (resolved) return resolved;
  }

  return null;
}
